/**
 * ErrorFallback — pantalla que Sentry.ErrorBoundary renderiza cuando
 * un error React no manejado escapa del árbol (Sprint 7 B4).
 *
 * Decisiones:
 * - Texto simple en español, en línea con la voz de marca.
 * - Acción principal: recargar la página (75% de los errores React se
 *   resuelven con una recarga: state corrupto, race condition transient).
 * - Acción secundaria: volver al inicio. Si el bug es en una ruta
 *   específica (`/plan/sesion/<id>` con datos malformados, p.ej.),
 *   volver a `/` saca al user del estado roto.
 * - El stack trace y el error se reportan SOLOS via Sentry — el user
 *   NO ve detalles técnicos.
 */
import { useTransition } from "react";

export function ErrorFallback() {
  // useTransition por si el reload se hace mientras hay otras
  // actualizaciones en curso (no debería pasar — el árbol está roto —
  // pero defensivo).
  const [, startTransition] = useTransition();

  function onReload() {
    startTransition(() => {
      window.location.reload();
    });
  }

  function onHome() {
    startTransition(() => {
      window.location.assign("/");
    });
  }

  return (
    <main
      data-testid="error-fallback"
      className="mx-auto flex min-h-app max-w-md flex-col items-center justify-center gap-6 bg-graphite p-6 text-center text-bone"
    >
      <header className="flex flex-col gap-3">
        <p className="text-xs uppercase tracking-widest text-gray-mid">nexum</p>
        <h1 className="text-2xl font-medium tracking-tight">
          Algo ha ido mal.
        </h1>
        <p className="text-sm text-bone-muted">
          Recarga la página o vuelve al inicio. Hemos registrado el error
          y lo revisaremos.
        </p>
      </header>

      <div className="flex flex-col items-stretch gap-3">
        <button
          type="button"
          onClick={onReload}
          className="rounded-md bg-lime px-5 py-2.5 text-sm font-medium text-graphite transition-colors duration-fast hover:bg-lime/90"
        >
          Recargar
        </button>
        <button
          type="button"
          onClick={onHome}
          className="rounded-md border border-border-subtle px-5 py-2.5 text-sm text-bone-muted transition-colors duration-fast hover:border-border-strong hover:text-bone"
        >
          Volver al inicio
        </button>
      </div>
    </main>
  );
}
